import { invalidateReviewCaches } from '../utils/homeFeedCache'
import { deleteReviewMessage } from '../utils/reviewDeleteMessage'
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from 'react'
import { useSearchParams, useNavigate, useNavigationType } from 'react-router-dom'
import AppBar from '../components/AppBar'
import FeedReviewBox from '../components/FeedReviewBox'
import Tab, { TabItem } from '../components/Tab'
import ReviewSlideCard from '../components/ReviewSlideCard'
import LoadingSpinner from '../components/LoadingSpinner'
import TextListBottomSheet from '../components/TextListBottomSheet'
import { ReviewProps, EarlierVisit } from '../types/review'
import { reviewApi } from '../api/review'
import { VisitType } from '../constants/visitType'
import { userApi, UserProfile } from '../api/user'
import { dialogUtil } from '../utils/dialog'
import { useCategoryStore } from '../stores/categoryIconStore'
import { ReviewListType } from '../constants/reviewListType'
import { ReviewViewType } from '../constants/reviewViewType'
import SearchBar from '../components/SearchBar'
import Gap from '../components/Gap'

/**
 * 화면별 목록 캐시.
 *
 * 리뷰를 눌러 상세로 갔다 뒤로 오면 컴포넌트가 다시 만들어지면서 목록을
 * 처음부터 받아온다. 그동안 로딩 화면이 떠서 스크롤 위치를 되살려도
 * "돌아왔다"는 느낌이 안 난다. 마지막 목록을 들고 있다가 즉시 그리고
 * 최신 내용은 뒤에서 조용히 받아온다.
 *
 * 화면 이동은 페이지 새로고침이 아니라 앱 내부 전환이라 이 값이 유지된다.
 */
const listCache = new Map<string, ReviewProps[]>()
/**
 * 어디까지 받아뒀는지. 목록만 되살리고 이걸 안 되살리면 쪽 번호가 1로
 * 시작해서, 바닥 감지가 **이미 가진 2쪽을 또** 받는다.
 */
const listPagingCache = new Map<string, { page: number; hasMore: boolean }>()

const Reviews: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  /*
   * 보던 자리로 되돌릴지, 맨 위에서 시작할지.
   *
   * 기록을 눌러 상세로 갔다 '뒤로'(POP) 오면 보던 자리에 있어야 한다.
   * 반면 프로필에서 '경험한 장소'를 눌러 새로 들어오면(PUSH) 맨 위가 맞다 —
   * 지난번에 어디까지 봤는지는 지금 보려는 것과 상관이 없다.
   *
   * 예전에는 저장된 값이 있으면 무조건 되돌려서, 새로 들어와도 엉뚱한
   * 중간 위치에서 시작했다.
   */
  const isBackNavigation = useNavigationType() === 'POP'
  /**
   * 캐시된 목록이 있으면 첫 렌더부터 그대로 그린다(로딩 화면을 거치지 않는다).
   * 최신 내용은 평소대로 받아와 조용히 갈아끼운다.
   */
  /**
   * 목록을 구분하는 열쇠.
   *
   * 카테고리(cat)까지 넣는다. 안 넣으면 '카페'에서 보던 위치를 '전체'가
   * 덮어써서, 뒤로 왔을 때 엉뚱한 자리로 간다.
   */
  const baseKey = `reviews_scroll:${searchParams.get('userId') ?? 'me'}:${searchParams.get('type') ?? 'all'}`

  /** 지금 주소에 적힌 검색어 (없으면 빈 문자열) */
  const urlQuery = (searchParams.get('q') ?? '').trim()

  /**
   * 스크롤 위치용 열쇠 — 카테고리와 **검색어**별로 따로 기억한다.
   *
   * 검색어를 넣는 이유는 카테고리를 넣은 이유와 같다. 결과가 두 개뿐인
   * 검색 목록에서 잡은 위치를, 수십 개짜리 전체 목록이 물려받으면 엉뚱한
   * 자리로 간다. 열쇠가 달라지면 위쪽 effect 가 맨 위로 되돌려 준다.
   */
  const listKeyFor = (tab: string) =>
    `${baseKey}:${searchParams.get('cat') ?? 'all'}:${tab}${
      urlQuery ? `:q=${urlQuery}` : ''
    }`

  /**
   * 목록 캐시용 열쇠 — 카테고리까지 넣는다.
   *
   * 예전에는 넣지 않았다. 전체를 한 번에 받아두고 거르기는 화면에서 했으니
   * 목록이 한 벌뿐이었다. 이제는 서버가 걸러 주므로 필터마다 다른 목록이고,
   * 열쇠가 같으면 '카페'에서 담은 것을 '전체'가 받아 읽는다.
   */
  const listCacheKey = `${baseKey}:${ReviewViewType.LIST}:${searchParams.get('cat') ?? 'all'}`


  /*
   * 캐시를 **쓸 때와 같은 열쇠로 읽는다.**
   *
   * 아래 fetch 는 검색 중이면 `…:q=검색어` 로 담는다. 그런데 읽을 때는
   * 기본 열쇠만 썼다. 예전에는 검색어가 늘 빈 채로 시작해서 두 열쇠가
   * 같았는데, 검색어를 주소에 두면서 전제가 깨졌다.
   *
   * 그래서 뒤로 왔을 때 **전체 목록 캐시가 먼저 한 번 그려졌다가**
   * 검색 결과가 도착하면 바뀌었다 — 관계없는 목록이 깜빡이는 증상.
   */
  const initialCacheKey = urlQuery
    ? `${listCacheKey}:q=${urlQuery}`
    : listCacheKey
  const [reviews, setReviews] = useState<ReviewProps[]>(
    () => listCache.get(initialCacheKey) ?? [],
  )

  /**
   * 목록 스크롤 위치 보존.
   *
   * 리뷰를 눌러 상세로 갔다 뒤로 오면 보던 자리에 그대로 있어야 한다.
   * 화면이 여러 종류(경험한 장소 / 저장한 장소, 내 것 / 남의 것)라 키를
   * 사용자·목록종류·보기모드로 구분한다. 안 그러면 서로 위치를 덮어쓴다.
   */
  const listScrollRef = useRef<HTMLDivElement | null>(null)
  const hasRestoredScrollRef = useRef(false)
  const [activeTab, setActiveTab] = useState<string>(ReviewViewType.LIST)

  const scrollStorageKey = listKeyFor(activeTab)

  // 복원할 위치로 옮기기 전까지 목록을 감춘다.
  // 감추지 않으면 맨 위가 한 번 그려진 뒤 튀어서 깜빡인다.
  const [isRestoringScroll, setIsRestoringScroll] = useState(
    () =>
      isBackNavigation &&
      !!sessionStorage.getItem(listKeyFor(ReviewViewType.LIST)),
  )

  /**
   * 카테고리를 바꾸면 목록 내용이 달라지므로 맨 위로 올린다.
   *
   * 안 그러면 '카페'에서 아래쪽에 있다가 '술집'으로 바꿨을 때 그 위치가
   * 그대로 남고, 그 값이 술집의 위치로 저장돼 다음에 뒤로 왔을 때 엉뚱한
   * 자리로 간다. 첫 렌더(복원)에는 건드리지 않는다.
   */
  const prevScrollKeyRef = useRef(scrollStorageKey)
  useEffect(() => {
    if (prevScrollKeyRef.current === scrollStorageKey) return
    prevScrollKeyRef.current = scrollStorageKey
    const el = listScrollRef.current
    if (el) el.scrollTop = 0
    sessionStorage.setItem(scrollStorageKey, '0')
  }, [scrollStorageKey])

  // 스크롤 위치 저장 (복원 완료 전에는 0이 덮어쓰지 않게 막는다)
  useEffect(() => {
    const el = listScrollRef.current
    if (!el) return
    const onScroll = () => {
      if (!hasRestoredScrollRef.current) return
      sessionStorage.setItem(scrollStorageKey, String(el.scrollTop))
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
    // reviews.length가 필요하다. 첫 렌더에는 로딩 화면이라 컨테이너가 없어
    // el이 null이고, 목록이 그려진 뒤 다시 붙어야 리스너가 실제로 걸린다.
  }, [scrollStorageKey, reviews.length])

  /**
   * 목록이 그려지자마자, 화면에 칠하기 전에 위치를 잡는다.
   * requestAnimationFrame으로 하면 이미 한 번 그린 뒤라 상단이 노출된다.
   */
  useLayoutEffect(() => {
    if (hasRestoredScrollRef.current) return
    if (reviews.length === 0) return

    const el = listScrollRef.current
    if (!el) return

    const finish = () => {
      hasRestoredScrollRef.current = true
      setIsRestoringScroll(false)
    }

    // 새로 들어온 경우(PUSH)에는 저장된 값이 있어도 맨 위에서 시작한다.
    // 남겨두면 다음에 뒤로 왔을 때 되살아나므로 지운다.
    if (!isBackNavigation) {
      sessionStorage.removeItem(scrollStorageKey)
      finish()
      return
    }

    const saved = sessionStorage.getItem(scrollStorageKey)
    if (!saved) {
      finish()
      return
    }

    // 행 안의 이미지가 덜 로드돼 콘텐츠 높이가 모자라면 scrollTop이 잘린다.
    // 실제로 그 값이 됐는지 확인하고, 안 됐으면 높이가 자랄 때 다시 시도한다.
    const target = parseInt(saved, 10)
    const apply = () => {
      el.scrollTop = target
      return Math.abs(el.scrollTop - target) <= 1
    }
    if (apply()) {
      finish()
      return
    }

    const observer = new ResizeObserver(() => {
      if (apply()) {
        observer.disconnect()
        finish()
      }
    })
    observer.observe(el.firstElementChild ?? el)
    const timer = setTimeout(() => {
      observer.disconnect()
      finish()
    }, 2000)
    return () => {
      observer.disconnect()
      clearTimeout(timer)
    }
  }, [reviews.length, scrollStorageKey, isBackNavigation])
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null)
  // 캐시가 있으면 로딩 화면을 거치지 않는다.
  const [isLoading, setIsLoading] = useState(
    () => !listCache.has(initialCacheKey),
  )
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  /**
   * 검색어. 바로 아래 cat 필터와 같은 이유로 **주소에 함께 둔다.**
   *
   * useState 만 쓰면, '퍼머넌트'로 찾아 놓고 기록 하나를 눌러 자세히보기로
   * 갔다 돌아왔을 때 화면이 새로 만들어지면서 검색이 풀려 전체 목록이 떴다.
   * 뒤로가기가 두 칸 물러난 것처럼 보이지만 실제로는 한 칸이고, 돌아온
   * 화면이 처음 상태로 다시 그려진 것이다(화면 녹화로 확인).
   *
   * 주소에 있으면 브라우저가 뒤로가기로 그대로 되돌려준다.
   */
  const [searchQuery, setSearchQuery] = useState<string>(
    () => searchParams.get('q') ?? ''
  )
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [slideReviews, setSlideReviews] = useState<ReviewProps[]>([])
  const [isSlideLoading, setIsSlideLoading] = useState(false)
  // 목록 캐시와 짝이다. 둘 중 하나만 되살리면 어긋난다.
  const [currentPage, setCurrentPage] = useState(
    () => listPagingCache.get(initialCacheKey)?.page ?? 1,
  )
  const [hasMore, setHasMore] = useState(
    () => listPagingCache.get(initialCacheKey)?.hasMore ?? false,
  )
  /**
   * 경험한 장소 카테고리 필터. null = 전체(아무 탭도 선택 안 됨)
   *
   * 컴포넌트 상태가 아니라 주소에 둔다. 예전에는 useState라, 글을 눌러
   * 상세로 갔다 뒤로 오면 화면이 다시 만들어지면서 '전체'로 풀렸다.
   * 주소에 있으면 브라우저가 뒤로가기로 그대로 되돌려준다.
   */
  const selectedCategory = searchParams.get('cat')
  // 돋보기 아이콘으로 토글하는 검색바 노출 여부
  const [isSearchVisible, setIsSearchVisible] = useState(
    () => !!searchParams.get('q')
  )
  /*
   * 서버로 보내는 검색어는 한 박자 늦춘다.
   *
   * SearchBar는 글자마다 알려준다. 그대로 보내면 '도르리' 네 글자에 요청이
   * 네 번 나가고, 늦게 온 응답이 먼저 온 것을 덮어 엉뚱한 목록이 남는다.
   */
  const [searchTerm, setSearchTerm] = useState(
    () => (searchParams.get('q') ?? '').trim()
  )
  useEffect(() => {
    const id = setTimeout(() => setSearchTerm(searchQuery.trim()), 300)
    return () => clearTimeout(id)
  }, [searchQuery])

  /*
   * 가라앉은 검색어를 주소에 적어 둔다 — 이게 있어야 뒤로 돌아왔을 때 복원된다.
   *
   * replace 로 적는다. push 면 한 글자 칠 때마다 히스토리가 쌓여서, 뒤로가기를
   * 글자 수만큼 눌러야 화면을 벗어난다.
   *
   * 다른 파라미터(type·userId·cat)를 보존하려고 기존 값을 복사해서 고친다.
   * 같은 값이면 쓰지 않는다 — 안 막으면 setSearchParams 가 만든 새 객체 때문에
   * 이 effect 가 자기 자신을 다시 부른다.
   */
  useEffect(() => {
    const current = searchParams.get('q') ?? ''
    if (current === searchTerm) return
    const next = new URLSearchParams(searchParams)
    if (searchTerm) next.set('q', searchTerm)
    else next.delete('q')
    setSearchParams(next, { replace: true })
  }, [searchTerm, searchParams, setSearchParams])

  const getAllCategories = useCategoryStore((state) => state.getAllCategories)
  // 카테고리가 로드되면 리렌더되도록 초기화 여부를 구독
  const isCategoryInitialized = useCategoryStore((state) => state.isInitialized)
  // "경험한 장소"(REVIEWS) 화면에서만 카테고리 탭 노출

  const loadParamsRef = useRef<{
    userId: string
    categoryId?: string
    type?: string
  } | null>(null)
  const isLoadingRef = useRef(false)
  const hasMoreRef = useRef(false)
  const currentPageRef = useRef(1)
  const loadReviewsRef = useRef<
    ((page: number, reset: boolean) => Promise<void>) | null
  >(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null)
  const loadMoreIoRef = useRef<IntersectionObserver | null>(null)

  hasMoreRef.current = hasMore
  currentPageRef.current = currentPage

  /*
   * 서버로 넘길 필터를 ref로도 들고 있는다.
   *
   * 다음 쪽을 부르는 쪽이 IntersectionObserver라, 콜백이 만들어진 시점의
   * 값을 그대로 쥐고 있으면 필터를 바꾼 뒤에도 옛 조건으로 더 받아온다.
   */
  const categoryRef = useRef<string | null>(selectedCategory)
  const termRef = useRef('')
  categoryRef.current = selectedCategory
  termRef.current = searchTerm

  /** 첫 로드는 아래 프로필 효과가 맡는다. 필터 효과가 겹쳐 두 번 부르지 않게. */
  const didInitialLoadRef = useRef(false)

  const tabItems: TabItem[] = [
    { id: ReviewViewType.LIST, label: '리스트로 보기' },
    { id: ReviewViewType.SLIDE, label: '이미지로 보기' },
  ]

  const toReviewProps = useCallback(
    (review: {
      id: string
      placeId: number | null
      placeAddress?: string
      user: {
        nickname: string
        username?: string | null
        profileImage?: string | null
        userId?: string | null
      }
      restaurant: { name: string; rating: number; category: string }
      reviewText: string | null
      minPricePerPerson?: number | null
      maxPricePerPerson?: number | null
      taggedFriends?: Array<{ id: number; nickname: string }>
      keywords?: Array<{ id: number; name: string }>
      images: string[]
      imageBlurs?: (string | null)[]
      likes: number
      isLiked: boolean
      comments: number
      date: string
      dateDisplay?: string
      visitType: VisitType
      isPublic?: boolean
      visitNumber?: number
      earlierVisits?: EarlierVisit[]
      isFollowing: boolean
      isBookmarked: boolean
      hasUserReview?: boolean
      hideScore?: boolean
    }) =>
      ({
        id: review.id,
        placeId: review.placeId as number,
        placeAddress: review.placeAddress ?? '',
        user: {
          nickname: review.user.nickname,
          username: review.user.username ?? undefined,
          profileImage: review.user.profileImage || undefined,
        },
        restaurant: {
          name: review.restaurant.name,
          rating: review.restaurant.rating,
          category: review.restaurant.category,
        },
        reviewText: review.reviewText,
        minPricePerPerson: review.minPricePerPerson ?? null,
        maxPricePerPerson: review.maxPricePerPerson ?? null,
        taggedFriends: review.taggedFriends ?? [],
        keywords: review.keywords ?? [],
        images: review.images,
        /*
         * 작은 판을 같이 들고 간다.
         *
         * 사진을 누르면 primePhotoViewer가 이 값을 뷰어 자리에 미리 놓는데,
         * 여기서 빠뜨려서 뷰어가 **빈 배열**을 받고 있었다. 그러면 뷰어는
         * 비율을 모른 채 첫 그림을 그리고, 잠시 뒤 원본이 와서 비율을 알면
         * 자리가 바뀐다 — 4:5가 아닌 사진이 살짝 커지는 것이 이것이다
         * (2026-10-20, 한식바 도르리 첫 장). 흰 번쩍임을 막는 것도 같이
         * 쉬고 있었다. 메인 피드는 넘기고 있어서 그쪽만 멀쩡했다.
         */
        imageBlurs: review.imageBlurs,
        likes: review.likes,
        isLiked: review.isLiked,
        comments: review.comments,
        date: review.date,
        dateDisplay: review.dateDisplay,
        visitType: review.visitType as VisitType,
        isPublic: review.isPublic,
        visitNumber: review.visitNumber,
        earlierVisits: review.earlierVisits,
        isFollowing: review.isFollowing,
        userId: review.user.userId || undefined,
        isBookmarked: review.isBookmarked || false,
        hasUserReview: review.hasUserReview || false,
        hideScore: review.hideScore || false,
      }) as ReviewProps,
    [],
  )

  /**
   * @param keepExisting 1쪽을 받되 **목록을 갈아끼우지 않고 내용만 갱신**한다.
   *
   * 뒤로 왔을 때 쓴다. 전에는 돌아올 때마다 `reset` 으로 목록을 1쪽(12건)
   * 으로 통째로 바꿨다. 그러면 캐시로 되살린 30건이 12건으로 줄면서
   * **스크롤이 바닥으로 잘려**, 보던 자리가 아니라 12번째 기록이 나타났다.
   * (당도를 보다 돌아오면 바로 앞 파브스커피가 뜨던 증상 — 2026-11-04)
   *
   * 길이와 차례를 그대로 두고 같은 id 의 내용만 바꾸므로 높이가 안 변한다.
   * 돌아온 사이에 **새로 생긴 기록은 안 붙는다** — 그건 새로 들어올 때
   * (PUSH) 받는다. 뒤로 오는 길에 목록이 길어지면 역시 자리가 밀린다.
   */
  const loadReviews = useCallback(
    async (page: number, reset: boolean, keepExisting: boolean = false) => {
      const params = loadParamsRef.current
      if (!params || isLoadingRef.current) return

      if (reset && !keepExisting) {
        setIsLoading(true)
      } else if (!reset) {
        setIsLoadingMore(true)
      }
      isLoadingRef.current = true

      try {
        /*
         * 경험한 장소도 나눠 받는다.
         *
         * 예전에는 전체를 한 번에 받아 화면에서 걸렀다. 주석에는 '백엔드가
         * categoryId를 지원하지 않아서'라고 적혀 있었는데 사실이 아니었다 —
         * 컨트롤러에 그 필터가 있다. 진짜로 막고 있던 것은 검색이었다.
         * 가게 이름 검색을 서버로 옮기고 나서 나눠 받게 됐다.
         *
         * 기록 346개인 계정에서 한 번에 1MB(서버 483ms)였다 (2026-10-20 실측).
         */
        const reviewResponse = await reviewApi.getExperienceByUserId(
          params.userId,
          undefined,
          page,
          false,
          categoryRef.current ?? undefined,
          termRef.current || undefined,
        )
        const converted = reviewResponse.reviews
          .filter((r) => r.placeId !== null)
          .map((r) => toReviewProps(r))

        /*
         * 검색 중에는 검색어까지 갈라 담는다 — 서버가 걸러서 보내므로
         * 검색 결과와 전체 목록은 다른 벌이다.
         * (처음 그릴 때는 검색어가 늘 비어 있어 기본 열쇠와 맞는다)
         */
        const cacheKey = termRef.current
          ? `${listCacheKey}:q=${termRef.current}`
          : listCacheKey
        if (reset && keepExisting) {
          // 같은 id 는 새 내용으로 바꾸고, 길이와 차례는 건드리지 않는다.
          setReviews((prev) => {
            if (prev.length === 0) {
              listCache.set(cacheKey, converted)
              return converted
            }
            const fresh = new Map(converted.map((r) => [r.id, r]))
            const merged = prev.map((r) => fresh.get(r.id) ?? r)
            listCache.set(cacheKey, merged)
            return merged
          })
        } else if (reset) {
          setReviews(converted)
          listCache.set(cacheKey, converted)
        } else {
          /*
           * 이미 있는 기록은 버리고 붙인다.
           *
           * 같은 쪽을 두 번 받으면 그대로 두 번 그려졌다 — 아라님 목록 끝의
           * 피에스·물론이 두 번 보이던 것 (2026-10-31). 아래 ref 를 바로 맞춰
           * 두 번 받는 길은 막았지만, 목록이 어긋날 길은 또 생길 수 있어
           * 마지막 방어선으로 둔다 (홈 피드와 같은 방식).
           */
          setReviews((prev) => {
            const seen = new Set(prev.map((r) => r.id))
            const fresh = converted.filter((r) => !seen.has(r.id))
            const merged = fresh.length === 0 ? prev : [...prev, ...fresh]
            listCache.set(cacheKey, merged)
            return merged
          })
        }
        /*
         * 페이지 번호·더있음은 ref 도 지금 바로 맞춘다.
         *
         * 렌더 때만 맞추면(currentPageRef.current = currentPage) 아래 finally 가
         * '불러오는 중'을 푼 뒤 화면이 다시 그려지기 전 틈에 바닥 감지가 걸려
         * 옛 번호로 방금 받은 쪽을 또 요청한다. 서버 기록으로 2쪽 이후 요청의
         * 27%가 이렇게 두 번씩 나가고 있었다 (2026-10-17~31).
         */
        // 길이를 유지하는 새로고침은 1쪽만 다시 읽은 것이라, 어디까지
        // 받아뒀는지를 1로 되돌리면 안 된다. 되돌리면 바닥 감지가 이미 가진
        // 2쪽을 다시 받고 그만큼 목록이 들썩인다.
        if (!keepExisting) {
          const nextPage = reviewResponse.current_page ?? page
          const nextHasMore = reviewResponse.has_more ?? false
          currentPageRef.current = nextPage
          hasMoreRef.current = nextHasMore
          setCurrentPage(nextPage)
          setHasMore(nextHasMore)
          listPagingCache.set(cacheKey, { page: nextPage, hasMore: nextHasMore })
        }
      } catch (error) {
        console.error('리뷰 목록 로드 실패:', error)
        // 길이를 유지하는 새로고침이 실패하면 **되살린 목록을 그대로 둔다.**
        // 비우면 뒤로 왔다가 통신이 한 번 어긋난 것만으로 화면이 빈다.
        if (reset && !keepExisting) setReviews([])
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
        isLoadingRef.current = false
      }

      if (!reset) {
        requestAnimationFrame(() => {
          const sentinel = loadMoreSentinelRef.current
          const io = loadMoreIoRef.current
          if (!sentinel || !io || !hasMoreRef.current || isLoadingRef.current) {
            return
          }
          io.unobserve(sentinel)
          io.observe(sentinel)
        })
      }
    },
    [toReviewProps, listCacheKey],
  )

  const loadAllForSlide = useCallback(async () => {
    const params = loadParamsRef.current
    if (!params) return

    setIsSlideLoading(true)
    try {
      const reviewResponse = await reviewApi.getExperienceByUserId(
        params.userId,
        params.categoryId,
        1,
        true,
      )
      const converted = reviewResponse.reviews
        .filter((r) => r.placeId !== null)
        .map((r) => toReviewProps(r))
      setSlideReviews(converted)
      setCurrentSlideIndex(0)
    } catch (error) {
      console.error('이미지 보기용 리뷰 로드 실패:', error)
      setSlideReviews([])
    } finally {
      setIsSlideLoading(false)
    }
  }, [toReviewProps])

  loadReviewsRef.current = loadReviews

  const attachLoadMoreSentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      loadMoreSentinelRef.current = node
      if (loadMoreIoRef.current) {
        loadMoreIoRef.current.disconnect()
        loadMoreIoRef.current = null
      }
      if (!node) return

      const root = node.closest('[data-feed-scroll]')
      if (!root) return

      const io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (!entry?.isIntersecting) return
          if (isLoadingRef.current || !hasMoreRef.current) return
          const fetchMore = loadReviewsRef.current
          if (fetchMore) void fetchMore(currentPageRef.current + 1, false)
        },
        { root, threshold: 0 },
      )
      io.observe(node)
      loadMoreIoRef.current = io
    },
    [],
  )

  useEffect(() => {
    const typeFromQuery = searchParams.get('type')
    const userIdFromQuery = searchParams.get('userId')

    const loadCurrentUser = async () => {
      try {
        const response = await userApi.getProfile()
        setCurrentUser(response.user)

        // 저장한 장소가 이 화면을 쓰던 시절의 값. 지금은 늘 비어 있다.
        const initialCategoryId = undefined
        const userId = userIdFromQuery || response.user.id
        const type = typeFromQuery || undefined

        loadParamsRef.current = {
          userId,
          categoryId: initialCategoryId,
          type,
        }

        didInitialLoadRef.current = true
        /*
          뒤로 왔고 목록이 이미 있으면 **갈아끼우지 않고** 내용만 새로 읽는다.
          홈 피드(MainHome)가 스냅샷을 되살리고 1쪽을 다시 안 받는 것과 같은 뜻이다.
        */
        loadReviews(1, true, isBackNavigation && listCache.has(initialCacheKey))
      } catch (error) {
        console.error('사용자 정보 로드 실패:', error)
      }
    }

    loadCurrentUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * 카테고리·검색어가 바뀌면 처음부터 다시 받는다.
   *
   * 예전에는 전부 받아둔 터라 바뀌어도 다시 부를 일이 없었다.
   */
  useEffect(() => {
    if (!didInitialLoadRef.current) return
    setCurrentPage(1)
    loadReviews(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchTerm])

  const handleDeleteReview = async (
    reviewId: string,
    visitNumber?: number | null,
  ) => {
    const confirmed = await dialogUtil.confirm({
      message: deleteReviewMessage(visitNumber),
      confirmText: '삭제',
      cancelText: '취소',
    })

    if (!confirmed) return

    try {
      await reviewApi.deleteReview(parseInt(reviewId))
      // 지운 기록이 홈 피드·랭킹 캐시에 남아 있으면 계속 보인다. 버린다.
      invalidateReviewCaches()
      setReviews((prevReviews) =>
        prevReviews.filter((review) => review.id !== reviewId),
      )
      setSlideReviews((prev) => {
        const next = prev.filter((review) => review.id !== reviewId)
        setCurrentSlideIndex((i) =>
          next.length === 0 ? 0 : Math.min(i, next.length - 1),
        )
        return next
      })
    } catch (error) {
      console.error('리뷰 삭제 실패:', error)
    }
  }

  const handleEditReview = () => {
    if (activeTab === ReviewViewType.SLIDE && slideReviews[currentSlideIndex]) {
      const review = slideReviews[currentSlideIndex]
      navigate(`/store-review/${review.placeId}`)
    }
  }

  const handleDeleteFromBottomSheet = () => {
    if (activeTab === ReviewViewType.SLIDE && slideReviews[currentSlideIndex]) {
      handleDeleteReview(
        slideReviews[currentSlideIndex].id,
        slideReviews[currentSlideIndex].visitNumber,
      )
    }
  }

  const handleOpenBottomSheet = () => {
    setIsBottomSheetOpen(true)
  }

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false)
  }

  const handleSlideIndexChange = (index: number) => {
    setCurrentSlideIndex(index)
  }

  // 카테고리 탭 선택: 같은 탭을 다시 누르면 전체(null)로 토글
  // (전체 리뷰는 이미 로드돼 있으므로 클라이언트에서 필터만 적용 — 재요청 없음)
  const handleCategorySelect = (code: string) => {
    const next = new URLSearchParams(searchParams)
    if (selectedCategory === code) {
      next.delete('cat')
    } else {
      next.set('cat', code)
    }
    // replace로 바꾼다. push하면 필터를 세 번 눌렀을 때 뒤로가기를 세 번
    // 해야 화면을 벗어나게 된다.
    setSearchParams(next, { replace: true })
  }

  // 돋보기: 검색바 열기/닫기 (닫을 때 검색어 초기화)
  const handleToggleSearch = () => {
    setIsSearchVisible((prev) => {
      if (prev) setSearchQuery('')
      return !prev
    })
  }

  const renderAppBar = () => {
    const typeFromQuery = searchParams.get('type')
    const userIdFromQuery = searchParams.get('userId')

    let title = '기록'

    if (typeFromQuery === ReviewListType.REVIEWS) {
      title = '경험한 장소'
    }

    // 슬라이드 탭이고 현재 리뷰가 본인 것인 경우 수정 버튼 표시
    const isSlideTab = activeTab === ReviewViewType.SLIDE
    const currentReview =
      isSlideTab && slideReviews[currentSlideIndex]
        ? slideReviews[currentSlideIndex]
        : null
    const canEdit =
      isSlideTab &&
      currentReview &&
      currentUser &&
      currentReview.userId === currentUser.id

    // 타 유저 화면이면 랭킹 버튼 표시
    // currentUser가 로딩된 뒤에만 판단 (로딩 중 깜빡임 방지)
    const isOtherUser =
      !!userIdFromQuery && !!currentUser && userIdFromQuery !== currentUser.id
    const showRankingButton = isOtherUser

    const handleGoRanking = () => {
      navigate(`/main-ranking?userId=${userIdFromQuery}`)
    }

    let rightElement: React.ReactNode = undefined
    if (canEdit) {
      rightElement = (
        <button
          type="button"
          onClick={handleOpenBottomSheet}
          className="font-pretendard font-bold text-16px leading-24px text-[#6B5E46] pr-12px hover:opacity-70"
        >
          수정
        </button>
      )
    } else if (showRankingButton) {
      rightElement = (
        <button
          type="button"
          onClick={handleGoRanking}
          className="font-pretendard font-bold text-16px leading-24px text-[#6B5E46] pr-12px hover:opacity-70"
        >
          랭킹
        </button>
      )
    }

    return <AppBar title={title} isBottomBorder rightElement={rightElement} />
  }

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    if (tabId === ReviewViewType.SLIDE) {
      void loadAllForSlide()
    }
  }

  const renderTab = () => {
    return (
      <div className="app-container">
        <Tab
          items={tabItems}
          activeTabId={activeTab}
          onTabChange={handleTabChange}
        />
      </div>
    )
  }

  /*
   * 거르는 일은 전부 서버가 한다(분류 탭과 돋보기 모두). 여기서 또 거르면
   * 받아온 12건만 놓고 거르게 되어, 다음 쪽에 있는 것이 없는 것처럼 보인다.
   */
  const filteredReviews = reviews

  // 경험한 장소: 카테고리 탭(레스토랑/카페/술집) + 돋보기 검색
  const renderFilterBar = () => {
    const categories = isCategoryInitialized ? getAllCategories() : []
    return (
      <div className="app-container">
        <div className="w-full px-20px flex items-center justify-between pt-16px pb-8px">
          {/* 카테고리 탭 */}
          <div className="flex items-center gap-24px min-w-0 overflow-x-auto scrollbar-hide">
            {categories.map((cat) => {
              const isSelected = selectedCategory === cat.code
              return (
                <button
                  key={cat.code}
                  type="button"
                  onClick={() => handleCategorySelect(cat.code)}
                  className={`
                    font-pretendard text-16px leading-125% whitespace-nowrap transition-all
                    ${
                      isSelected
                        ? 'font-bold text-textColorText2'
                        : 'font-regular text-textColorText3'
                    }
                  `}
                >
                  {cat.name}
                </button>
              )
            })}
          </div>

          {/* 돋보기 아이콘 */}
          <button
            type="button"
            onClick={handleToggleSearch}
            className="shrink-0 ml-12px"
          >
            <img
              src="/images/ranking-search-icon.png"
              alt="검색"
              className="w-28px h-28px object-contain"
            />
          </button>
        </div>

        {/* 검색바 (돋보기로 토글) */}
        {isSearchVisible && (
          <div className="w-full px-20px flex items-center pb-8px">
            <div className="min-w-0 flex-1">
              <SearchBar
                /*
                 * 서버가 가게 이름과 기록할 때 고른 키워드를 함께 본다
                 * (Review::scopeMatchingKeyword). 키워드는 앞에서부터
                 * 맞을 때만 걸리고, 다른 말은 config/review_keyword_aliases.php
                 * 에 적는다 — '펫'으로 '반려동물 동반'을 찾는 식이다.
                 */
                placeholder="매장 이름 또는 키워드로 검색"
                value={searchQuery}
                onSearch={(value) => setSearchQuery(value)}
              />
            </div>
            <Gap width={16} />
            <div
              className="font-pretendard font-bold text-16px leading-24px text-brown-80%-main cursor-pointer whitespace-nowrap"
              onClick={() => {
                setIsSearchVisible(false)
                setSearchQuery('')
              }}
            >
              닫기
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderReviewList = () => {
    return (
      <div
        ref={listScrollRef}
        data-feed-scroll
        className="app-container flex-1 overflow-y-auto scrollbar-hide"
        style={isRestoringScroll ? { visibility: 'hidden' } : undefined}
      >
        {filteredReviews.length > 0 ? (
          <div className="w-full flex flex-col">
            {filteredReviews.map((review) => (
              <FeedReviewBox
                key={review.id}
                review={review}
                hideScore={review.hideScore || false}
              />
            ))}
            {hasMore && (
              <>
                <Gap height={24} />
                <div
                  ref={attachLoadMoreSentinelRef}
                  className={`
                    w-full
                    min-h-80px
                    flex
                    flex-col
                    items-center
                    justify-center
                    py-24px
                  `}
                >
                  {isLoadingMore && (
                    <LoadingSpinner size={28} borderWidth={4} />
                  )}
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    )
  }

  const renderLoading = () => {
    return (
      <div className="flex-1 w-full flex items-center justify-center empty-state-center">
        <LoadingSpinner />
      </div>
    )
  }

  const renderReviewSlideCard = () => {
    // 빈 상태는 바깥의 absolute 오버레이가 그린다 (여기선 안 그림)
    if (slideReviews.length === 0) return null
    return (
      <div className="w-full app-container shrink-0">
        <ReviewSlideCard
          reviews={slideReviews}
          onCurrentIndexChange={handleSlideIndexChange}
        />
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* 헤더 */}
        {renderAppBar()}

        {/* 탭 섹션 */}
        {renderTab()}

        {/* 목록·이미지 공통 칸.
            빈 상태 기준 상자를 둘이 함께 쓴다 — 검색바가 '리스트로 보기'에만
            있어서 갈래마다 따로 두면 탭을 바꿀 때 문구가 34pt 튄다. */}
        <div className="flex-1 min-h-0 flex flex-col relative">
          {/* 리뷰 목록 */}
          {activeTab === ReviewViewType.LIST ? (
            <>
              {/* 경험한 장소: 카테고리 탭 + 돋보기 검색 (로딩 중에도 노출) */}
              {renderFilterBar()}
              {/* 목록이 이미 있으면 로딩 화면으로 덮지 않는다.
                캐시로 그린 뒤 최신 내용을 받아오는 동안 화면이 깜빡이면 안 된다. */}
              {isLoading && reviews.length === 0 ? (
                <>{renderLoading()}</>
              ) : (
                <>{renderReviewList()}</>
              )}
            </>
          ) : isLoading || isSlideLoading ? (
            <>{renderLoading()}</>
          ) : (
            <div
              className={`
              flex-1
              min-h-0
              w-full
              overflow-hidden
              scrollbar-hide
            `}
              // 이미지로 보기는 카드 한 장을 화면 중앙에 고정하고 좌우로 넘기는
              // 화면이다. 세로로 스크롤될 내용이 없는데 overflow-y-auto라 카드가
              // 위아래로 밀렸다. 세로 스크롤을 막고 터치는 좌우 넘김에만 쓰이게 한다.
              style={{ touchAction: 'pan-x', overscrollBehavior: 'contain' }}
            >
              <div
                className={`
                min-h-full
                flex
                flex-col
                justify-center
                items-center
                py-16px
              `}
              >
                <div className="-translate-y-32px w-full">
                  {renderReviewSlideCard()}
                </div>
              </div>
            </div>
          )}

          {/* 빈 상태: 비어 있는 칸의 광학 중앙(index.css의 .empty-state-center) */}
          {(activeTab === ReviewViewType.LIST
            ? !isLoading && reviews.length === 0
            : !isLoading && !isSlideLoading && slideReviews.length === 0) && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none empty-state-center">
              <p className="text-style-body1 text-textColorText3">
                아직 기록이 없습니다
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 바텀 시트 */}
      <TextListBottomSheet
        isOpen={isBottomSheetOpen}
        onClose={handleCloseBottomSheet}
        items={[
          {
            text: '기록 수정',
            onClick: handleEditReview,
          },
          {
            text: '기록 삭제',
            onClick: handleDeleteFromBottomSheet,
          },
        ]}
      />
    </div>
  )
}

export default Reviews
